import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FlagIcon from '@/components/FlagIcon';
import { cx } from '@/components/ui';
import {
  getStoredLanguage,
  normalizeLanguage,
  setStoredLanguage,
  type SupportedLanguage,
} from '@/i18n/language';
import { playClickSound } from '@/utils/soundUtils';

type Props = {
  compact?: boolean;
  className?: string;
};

type LanguageOption = {
  lang: SupportedLanguage;
  labelKey: string;
  fallback: string;
  countryCode: string;
};

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { lang: 'en', labelKey: 'settings.english', fallback: 'English', countryCode: 'US' },
  { lang: 'es', labelKey: 'settings.spanish', fallback: 'Español', countryCode: 'ES' },
];

export default function LanguageSwitcher({ compact = false, className = '' }: Props) {
  const { i18n, t } = useTranslation();
  const [lang, setLang] = useState<SupportedLanguage>(() => getStoredLanguage());
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const next = getStoredLanguage();
    setLang(next);
    if (normalizeLanguage(i18n.language) !== next) {
      void i18n.changeLanguage(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onLangChanged = (lng: string) => {
      setLang(normalizeLanguage(lng));
    };
    i18n.on('languageChanged', onLangChanged);
    return () => {
      i18n.off('languageChanged', onLangChanged);
    };
  }, [i18n]);

  const current = useMemo(
    () => LANGUAGE_OPTIONS.find((o) => o.lang === lang) ?? LANGUAGE_OPTIONS[0],
    [lang],
  );

  const apply = useCallback(
    (next: SupportedLanguage) => {
      playClickSound();
      setStoredLanguage(next);
      setLang(next);
      void i18n.changeLanguage(next);
      setOpen(false);
    },
    [i18n],
  );

  const updateMenuPos = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 8,
      right: Math.max(8, window.innerWidth - rect.right),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    window.addEventListener('resize', updateMenuPos);
    window.addEventListener('scroll', updateMenuPos, true);
    return () => {
      window.removeEventListener('resize', updateMenuPos);
      window.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [open, updateMenuPos]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const currentLabel = t(current.labelKey, current.fallback);

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={t('header.selectLanguage', 'Select language')}
            className="cosmos-dropdown cosmos-pop-in py-2 shadow-card min-w-[168px]"
            style={{
              position: 'fixed',
              top: menuPos.top,
              right: menuPos.right,
              zIndex: 1080,
            }}
          >
            {LANGUAGE_OPTIONS.map((opt) => {
              const active = lang === opt.lang;
              const label = t(opt.labelKey, opt.fallback);
              return (
                <button
                  key={opt.lang}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => apply(opt.lang)}
                  className={cx(
                    'flex w-full items-center gap-2.5 px-4 min-h-[44px] text-left transition-colors',
                    active ? 'bg-white/10' : 'hover:bg-white/5',
                  )}
                >
                  <FlagIcon countryCode={opt.countryCode} size="md" />
                  <span className="flex-1 font-ui text-[length:var(--text-body)] text-body-muted">
                    {label}
                  </span>
                  {active && <Check className="h-4 w-4 flex-shrink-0 text-money-in" aria-hidden />}
                </button>
              );
            })}
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      data-tour-id="post-login-tour.language"
      className={cx('relative flex-shrink-0', className)}
    >
      <button
        type="button"
        onClick={() => {
          playClickSound();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t('header.changeLanguage', 'Change language, current: {{language}}', {
          language: currentLabel,
        })}
        className={cx('hd-chip', compact ? 'gap-1' : undefined)}
      >
        <FlagIcon countryCode={current.countryCode} size={compact ? 'md' : 'lg'} />
        <span className="truncate max-w-[72px]">{currentLabel}</span>
        <ChevronDown
          className={cx(
            'flex-shrink-0 text-caption-muted transition-transform',
            compact ? 'w-3 h-3' : 'w-3.5 h-3.5',
            open && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
}
